import { Decimal } from '@snap-types/types/decimal';
import { z } from 'zod/v4';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

type ValidateContributionMarginParams = {
  contributionMargin: string | Decimal;
  quoteLineUuid: string;
  quotePriceUuid: string;
};

const validator = z.coerce
  .number({
    error: 'Contribution margin is required',
  })
  .gt(-100, 'Contribution margin must be greater than -100%')
  .lt(100, 'Contribution margin must be less than 100%');

export function validateContributionMargin(
  state: WritableQuoteBuilderStateType,
  {
    contributionMargin,
    quoteLineUuid,
    quotePriceUuid,
  }: ValidateContributionMarginParams,
) {
  const contributionMarginString = contributionMargin.toString();

  const invalidIssueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    type: 'invalid',
    field: 'margin',
  });

  const result = validator.safeParse(contributionMarginString);

  if (!result.success) {
    const error = z.flattenError(result.error).formErrors[0];

    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid contribution margin',
      description: error,
      severity: 'error',
      type: 'invalid',
      field: 'margin',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'margin',
        }),
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  return new Decimal(result.data);
}
